// Data Service Layer - Automatically switches between API and Mock Data
// If backend is not available, falls back to localStorage mock data

import api, { checkBackendHealth } from './api';
import { 
  DEMO_RESTAURANTS, 
  DEMO_CATEGORIES, 
  DEMO_MENU_ITEMS,
  DEMO_ORDERS,
  DEMO_COUPONS 
} from './mockData';

// Check if backend API is available
let isApiAvailable = false;
let apiCheckPerformed = false;

async function checkApiAvailability(): Promise<boolean> {
  if (apiCheckPerformed) {
    return isApiAvailable;
  }

  try {
    isApiAvailable = await checkBackendHealth();
  } catch (error) {
    console.warn('Backend API not available, using mock data');
    isApiAvailable = false;
  }

  apiCheckPerformed = true;
  return isApiAvailable;
}

// ============================================
// RESTAURANT DATA SERVICE
// ============================================

export const restaurantDataService = {
  getAll: async (filters?: any) => {
    const apiReady = await checkApiAvailability();
    
    if (apiReady) {
      try {
        const response = await api.restaurants.getAll(filters);
        return response.data;
      } catch (error) {
        console.warn('API call failed, falling back to mock data:', error);
      }
    }

    // Fallback to mock data
    let restaurants = DEMO_RESTAURANTS.filter(r => r.isActive);

    // Apply filters
    if (filters) {
      if (filters.city) {
        restaurants = restaurants.filter(r => r.city === filters.city);
      }
      if (filters.isPureVeg) {
        restaurants = restaurants.filter(r => r.isPureVeg);
      }
      if (filters.hasOffers) {
        restaurants = restaurants.filter(r => r.hasOffers);
      }
      if (filters.minRating) {
        restaurants = restaurants.filter(r => r.rating >= filters.minRating);
      }
    }

    return restaurants;
  },

  getById: async (id: string) => {
    const apiReady = await checkApiAvailability();
    
    if (apiReady) {
      try {
        const response = await api.restaurants.getById(id);
        return response.data;
      } catch (error) {
        console.warn('API call failed, falling back to mock data:', error);
      }
    }

    // Fallback to mock data
    return DEMO_RESTAURANTS.find(r => r.id === id);
  },

  search: async (query: string, city?: string) => {
    const apiReady = await checkApiAvailability();
    
    if (apiReady) {
      try {
        const response = await api.restaurants.getAll({ search: query, city } as any);
        return response.data;
      } catch (error) {
        console.warn('API call failed, falling back to mock data:', error);
      }
    }

    // Fallback to mock data
    const searchTerm = query.toLowerCase();
    return DEMO_RESTAURANTS.filter(r => 
      r.name.toLowerCase().includes(searchTerm) ||
      r.description.toLowerCase().includes(searchTerm) ||
      r.cuisine.some(c => c.toLowerCase().includes(searchTerm))
    );
  },
};

// ============================================
// MENU DATA SERVICE
// ============================================

export const menuDataService = {
  getByRestaurant: async (restaurantId: string, filters?: any) => {
    const apiReady = await checkApiAvailability();
    
    if (apiReady) {
      try {
        const response = await api.menu.getItems(restaurantId, filters?.categoryId);
        let items = response.data || [];

        if (filters) {
          if (filters.isActive !== undefined) {
            items = items.filter((item: any) => item.isActive === (filters.isActive === 'true'));
          }
          if (filters.tags) {
            const tagArray = String(filters.tags).split(',');
            items = items.filter((item: any) =>
              Array.isArray(item.tags) && tagArray.some((tag: string) => item.tags.includes(tag))
            );
          }
          if (filters.search) {
            const searchTerm = String(filters.search).toLowerCase();
            items = items.filter((item: any) =>
              String(item.name || '').toLowerCase().includes(searchTerm) ||
              String(item.description || '').toLowerCase().includes(searchTerm)
            );
          }
        }

        return items;
      } catch (error) {
        console.warn('API call failed, falling back to mock data:', error);
      }
    }

    // Fallback to mock data
    let items = DEMO_MENU_ITEMS.filter(item => item.restaurantId === restaurantId);

    if (filters) {
      if (filters.categoryId) {
        items = items.filter(item => item.categoryId === filters.categoryId);
      }
      if (filters.isActive !== undefined) {
        items = items.filter(item => item.isActive === (filters.isActive === 'true'));
      }
      if (filters.tags) {
        const tagArray = filters.tags.split(',');
        items = items.filter(item => 
          tagArray.some((tag: string) => item.tags.includes(tag))
        );
      }
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        items = items.filter(item => 
          item.name.toLowerCase().includes(searchTerm) ||
          item.description.toLowerCase().includes(searchTerm)
        );
      }
    }

    return items;
  },

  getById: async (id: string) => {
    const apiReady = await checkApiAvailability();
    
    if (apiReady) {
      try {
        const response = await (api as any).menu.getById?.(id);
        if (response?.data) return response.data;
      } catch (error) {
        console.warn('API call failed, falling back to mock data:', error);
      }
    }

    // Fallback to mock data
    return DEMO_MENU_ITEMS.find(item => item.id === id);
  },

  create: async (menuItemData: any) => {
    const apiReady = await checkApiAvailability();
    
    if (apiReady) {
      try {
        return await api.menu.createItem(menuItemData);
      } catch (error) {
        console.warn('API call failed, falling back to localStorage:', error);
      }
    }

    // Fallback: Save to localStorage
    const items = JSON.parse(localStorage.getItem('menu_items') || '[]');
    const newItem = {
      ...menuItemData,
      id: `item-${Date.now()}`,
    };
    items.push(newItem);
    localStorage.setItem('menu_items', JSON.stringify(items));
    return { success: true, data: newItem };
  },

  update: async (id: string, menuItemData: any) => {
    const apiReady = await checkApiAvailability();
    
    if (apiReady) {
      try {
        return await api.menu.updateItem(id, menuItemData);
      } catch (error) {
        console.warn('API call failed, falling back to localStorage:', error);
      }
    }

    // Fallback: Update in localStorage
    const items = JSON.parse(localStorage.getItem('menu_items') || '[]');
    const index = items.findIndex((item: any) => item.id === id);
    if (index !== -1) {
      items[index] = { ...items[index], ...menuItemData };
      localStorage.setItem('menu_items', JSON.stringify(items));
      return { success: true, data: items[index] };
    }
    throw new Error('Menu item not found');
  },

  delete: async (id: string) => {
    const apiReady = await checkApiAvailability();
    
    if (apiReady) {
      try {
        return await api.menu.deleteItem(id);
      } catch (error) {
        console.warn('API call failed, falling back to localStorage:', error);
      }
    }

    // Fallback: Delete from localStorage
    const items = JSON.parse(localStorage.getItem('menu_items') || '[]');
    const filtered = items.filter((item: any) => item.id !== id);
    localStorage.setItem('menu_items', JSON.stringify(filtered));
    return { success: true };
  },
};

// ============================================
// CATEGORY DATA SERVICE
// ============================================

export const categoryDataService = {
  getByRestaurant: async (restaurantId: string) => {
    const apiReady = await checkApiAvailability();
    
    if (apiReady) {
      try {
        const response = await api.menu.getCategories(restaurantId);
        return response.data;
      } catch (error) {
        console.warn('API call failed, falling back to mock data:', error);
      }
    }

    // Fallback to mock data
    return DEMO_CATEGORIES.filter(cat => cat.restaurantId === restaurantId);
  },

  create: async (categoryData: any) => {
    const apiReady = await checkApiAvailability();
    
    if (apiReady) {
      try {
        return await api.menu.createCategory(categoryData);
      } catch (error) {
        console.warn('API call failed, falling back to localStorage:', error);
      }
    }

    // Fallback: Save to localStorage
    const categories = JSON.parse(localStorage.getItem('categories') || '[]');
    const newCategory = {
      ...categoryData,
      id: `cat-${Date.now()}`,
    };
    categories.push(newCategory);
    localStorage.setItem('categories', JSON.stringify(categories));
    return { success: true, data: newCategory };
  },
};

// ============================================
// ORDER DATA SERVICE
// ============================================

export const orderDataService = {
  getAll: async (filters?: any) => {
    const apiReady = await checkApiAvailability();
    
    if (apiReady) {
      try {
        const response = await api.orders.getAll(filters);
        return response.data;
      } catch (error) {
        console.warn('API call failed, falling back to mock data:', error);
      }
    }

    // Fallback to mock data
    let orders = DEMO_ORDERS;

    if (filters?.status) {
      orders = orders.filter(o => o.status === filters.status);
    }

    return orders;
  },

  getById: async (id: string) => {
    const apiReady = await checkApiAvailability();
    
    if (apiReady) {
      try {
        const response = await api.orders.getById(id);
        return response.data;
      } catch (error) {
        console.warn('API call failed, falling back to mock data:', error);
      }
    }

    // Fallback to mock data
    return DEMO_ORDERS.find(o => o.id === id);
  },

  create: async (orderData: any) => {
    const apiReady = await checkApiAvailability();
    
    if (apiReady) {
      try {
        return await api.orders.create(orderData);
      } catch (error) {
        console.warn('API call failed, falling back to localStorage:', error);
      }
    }

    // Fallback: Save to localStorage
    const orders = JSON.parse(localStorage.getItem('orders') || '[]');
    const newOrder = {
      ...orderData,
      id: `order-${Date.now()}`,
      orderNumber: `ORD${Date.now()}`,
      status: 'pending',
      paymentStatus: 'pending',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    orders.push(newOrder);
    localStorage.setItem('orders', JSON.stringify(orders));
    return { success: true, data: newOrder };
  },

  updateStatus: async (id: string, status: string) => {
    const apiReady = await checkApiAvailability();
    
    if (apiReady) {
      try {
        return await api.orders.updateStatus(id, status);
      } catch (error) {
        console.warn('API call failed, falling back to localStorage:', error);
      }
    }

    // Fallback: Update in localStorage
    const orders = JSON.parse(localStorage.getItem('orders') || '[]');
    const index = orders.findIndex((o: any) => o.id === id);
    if (index !== -1) {
      orders[index].status = status;
      orders[index].updatedAt = new Date().toISOString();
      localStorage.setItem('orders', JSON.stringify(orders));
      return { success: true };
    }
    throw new Error('Order not found');
  },
};

// ============================================
// COUPON DATA SERVICE
// ============================================

export const couponDataService = {
  getByRestaurant: async (restaurantId: string) => {
    const apiReady = await checkApiAvailability();
    
    if (apiReady) {
      try {
        const response = await api.coupons.getByRestaurant(restaurantId, true);
        return response.data;
      } catch (error) {
        console.warn('API call failed, falling back to mock data:', error);
      }
    }

    // Fallback to mock data
    return DEMO_COUPONS.filter(c => c.restaurantId === restaurantId && c.isActive);
  },

  validate: async (code: string, restaurantId: string, orderValue: number) => {
    const apiReady = await checkApiAvailability();
    
    if (apiReady) {
      try {
        const response = await api.coupons.validate(code, restaurantId, orderValue);
        return response.data;
      } catch (error) {
        console.warn('API call failed, falling back to mock validation:', error);
      }
    }

    // Fallback: Mock validation
    const normalizedCode = String(code || '').trim().toUpperCase();
    const directMatch = DEMO_COUPONS.find(c =>
      String(c.code || '').toUpperCase() === normalizedCode &&
      c.restaurantId === restaurantId &&
      c.isActive &&
      orderValue >= c.minOrderValue
    );
    const fallbackMatch = DEMO_COUPONS.find(c =>
      String(c.code || '').toUpperCase() === normalizedCode &&
      c.isActive &&
      orderValue >= c.minOrderValue
    );
    const coupon = directMatch || fallbackMatch;

    if (!coupon) {
      throw new Error('Invalid coupon code');
    }

    let discount = 0;
    if (coupon.discountType === 'percentage') {
      discount = (orderValue * coupon.discountValue) / 100;
      if (coupon.maxDiscount) {
        discount = Math.min(discount, coupon.maxDiscount);
      }
    } else {
      discount = coupon.discountValue;
    }

    return { ...coupon, restaurantId, calculatedDiscount: discount };
  },
};

// ============================================
// BOOKING DATA SERVICE
// ============================================

export const bookingDataService = {
  create: async (bookingData: any) => {
    const apiReady = await checkApiAvailability();
    
    if (apiReady) {
      try {
        return await api.bookings.create(bookingData);
      } catch (error) {
        console.warn('API call failed, falling back to localStorage:', error);
      }
    }

    // Fallback: Save to localStorage
    const bookings = JSON.parse(localStorage.getItem('bookings') || '[]');
    const newBooking = {
      ...bookingData,
      id: `booking-${Date.now()}`,
      bookingNumber: `BKG${Date.now()}`,
      status: 'pending',
      createdAt: new Date().toISOString(),
    };
    bookings.push(newBooking);
    localStorage.setItem('bookings', JSON.stringify(bookings));
    return { success: true, data: newBooking };
  },

  getAll: async (filters?: any) => {
    const apiReady = await checkApiAvailability();
    
    if (apiReady) {
      try {
        const response = await api.bookings.getAll(filters);
        return response.data;
      } catch (error) {
        console.warn('API call failed, falling back to localStorage:', error);
      }
    }

    // Fallback: Get from localStorage
    const bookings = JSON.parse(localStorage.getItem('bookings') || '[]');
    return bookings;
  },
};

// Export a status checker
export const getApiStatus = async () => {
  const isAvailable = await checkApiAvailability();
  return {
    isApiAvailable: isAvailable,
    mode: isAvailable ? 'API' : 'Mock Data',
  };
};
